/**
 * NotificationsTab.js
 * ===================
 *
 * Settings > Notifications. Two stacked sections:
 *
 *   1. Delivery channels — cards for Slack / Microsoft Teams /
 *      PagerDuty / generic webhook endpoints the user has connected
 *      (add / edit / enable / test / remove). In-app, Email and
 *      WebSocket need no setup and are listed as always-available.
 *
 *   2. Events — one collapsible per category, one row per overridable
 *      event. Each row has a master on/off switch and per-channel icon
 *      toggles. Channels that require setup are disabled (with a
 *      pointer to section 1) until at least one endpoint of that type
 *      is connected.
 *
 * Catalog + defaults come from the BE so YAML edits show up without a
 * FE change. The user's picks are drafted locally and saved in one
 * PUT.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, IconButton,
  Paper, Stack, Switch, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import ChannelIcon, { CHANNEL_META } from './ChannelIcons';
import {
  fetchCatalog, fetchPreferences, updatePreferences,
  fetchChannels, saveChannel, deleteChannel, testChannel,
} from '../../redux/actions/notifications';


const SEVERITY_TONE = {
  info:     'info',
  success:  'success',
  warning:  'warning',
  critical: 'error',
};

const _toneColor = (theme, tone) => {
  switch (tone) {
    case 'info':    return theme.palette.info.main;
    case 'success': return theme.palette.success.main;
    case 'warning': return theme.palette.warning.main;
    case 'error':   return theme.palette.error.main;
    default:        return theme.palette.text.secondary;
  }
};

// Config fields shown in the add/edit dialog, per endpoint type.
const TYPE_FIELDS = {
  slack: [
    { key: 'url', label: 'Incoming webhook URL', placeholder: 'https://hooks.slack.com/services/…', required: true },
  ],
  teams: [
    { key: 'url', label: 'Workflow webhook URL', placeholder: 'https://…webhook.office.com/… or Power Automate URL', required: true },
  ],
  pagerduty: [
    { key: 'routing_key', label: 'Integration key (Events API v2)', placeholder: '32-character routing key', required: true },
  ],
  webhook: [
    { key: 'url',    label: 'Endpoint URL', placeholder: 'https://example.com/hooks/predictai', required: true },
    { key: 'secret', label: 'Signing secret (optional)', placeholder: 'Used for the X-PredictAI-Signature HMAC header', required: false },
  ],
};

const SETUP_TYPES = ['slack', 'teams', 'pagerduty', 'webhook'];


// ---------------------------------------------------------------------------
// Add / edit endpoint dialog
// ---------------------------------------------------------------------------

const EndpointDialog = ({ open, type, endpoint, saving, error, onClose, onSave }) => {
  const isEdit = !!endpoint;
  const fields = TYPE_FIELDS[type] || [];
  const [label, setLabel] = useState('');
  const [config, setConfig] = useState({});

  useEffect(() => {
    if (open) {
      setLabel(endpoint?.label || '');
      setConfig({});
    }
  }, [open, endpoint]);

  const canSave = isEdit
    // On edit, config fields may stay blank (server keeps the stored value).
    ? true
    : fields.every((f) => !f.required || (config[f.key] || '').trim());

  const handleSave = () => {
    const payload = {
      type,
      label,
      config: Object.fromEntries(
        Object.entries(config).filter(([, v]) => (v || '').trim()),
      ),
    };
    if (isEdit) payload.channel_id = endpoint.channel_id;
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <ChannelIcon type={type} size={22} />
        {isEdit ? 'Edit' : 'Connect'} {CHANNEL_META[type]?.label || type}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {CHANNEL_META[type]?.blurb}
          </Typography>
          <TextField
            size="small"
            label="Label"
            placeholder="e.g. Data team channel"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
          />
          {fields.map((f) => (
            <TextField
              key={f.key}
              size="small"
              label={f.label}
              placeholder={isEdit ? 'Leave blank to keep the current value' : f.placeholder}
              value={config[f.key] || ''}
              onChange={(e) => setConfig((prev) => ({ ...prev, [f.key]: e.target.value }))}
              fullWidth
              type={f.key === 'secret' || f.key === 'routing_key' ? 'password' : 'text'}
              autoComplete="off"
            />
          ))}
          {error && (
            <Typography variant="caption" color="error.main">{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          disabled={!canSave || saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : (isEdit ? 'Save' : 'Connect')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ---------------------------------------------------------------------------
// One connected endpoint row inside a channel card
// ---------------------------------------------------------------------------

const EndpointRow = ({ endpoint, test, onToggle, onEdit, onDelete, onTest }) => {
  const theme = useTheme();
  const last = endpoint.last_result;
  const testState = test || {};
  const health = testState.testing ? 'testing'
    : (testState.ok != null ? (testState.ok ? 'ok' : 'fail')
      : (last ? (last.ok ? 'ok' : 'fail') : null));
  const healthDetail = testState.detail || last?.detail || '';

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1.25, py: 0.75, borderRadius: 1,
        bgcolor: alpha(theme.palette.text.primary, 0.03),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
          {endpoint.label || CHANNEL_META[endpoint.type]?.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {endpoint.config_hint}
        </Typography>
      </Box>

      {health === 'testing' && <CircularProgress size={14} />}
      {health === 'ok' && (
        <Tooltip title={healthDetail || 'Last delivery succeeded'}>
          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
        </Tooltip>
      )}
      {health === 'fail' && (
        <Tooltip title={healthDetail || 'Last delivery failed'}>
          <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
        </Tooltip>
      )}

      <Tooltip title="Send a test message">
        <IconButton size="small" onClick={onTest} disabled={testState.testing}>
          <SendOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton size="small" onClick={onEdit}>
          <EditOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Remove">
        <IconButton size="small" onClick={onDelete}>
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
      <Switch
        size="small"
        checked={!!endpoint.enabled}
        onChange={(e) => onToggle(e.target.checked)}
      />
    </Stack>
  );
};


// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

const NotificationsTab = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const catalog          = useSelector((s) => s.notifications?.catalog);
  const preferences      = useSelector((s) => s.notifications?.preferences);
  const prefsLoading     = useSelector((s) => s.notifications?.prefsLoading);
  const channelsState    = useSelector((s) => s.notifications?.channels);
  const channels         = useMemo(() => channelsState || [], [channelsState]);
  const channelSaving    = useSelector((s) => s.notifications?.channelSaving);
  const channelSaveError = useSelector((s) => s.notifications?.channelSaveError);
  const channelTests     = useSelector((s) => s.notifications?.channelTests) || {};

  const [draft, setDraft] = useState(null);
  const [dialog, setDialog] = useState(null); // { type, endpoint|null }
  const [wasSaving, setWasSaving] = useState(false);

  useEffect(() => {
    if (!catalog) dispatch(fetchCatalog());
    if (!preferences) dispatch(fetchPreferences());
    dispatch(fetchChannels());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (preferences && !draft) setDraft(preferences.events || {});
  }, [preferences, draft]);

  // Close the endpoint dialog once a save completes without error.
  useEffect(() => {
    if (channelSaving) setWasSaving(true);
    else if (wasSaving) {
      setWasSaving(false);
      if (!channelSaveError) setDialog(null);
    }
  }, [channelSaving, wasSaving, channelSaveError]);

  const channelDefs = catalog?.channels || [
    { key: 'in_app', label: 'In-app', requires_setup: false },
    { key: 'email',  label: 'Email',  requires_setup: false },
  ];

  // type -> configured endpoint count (enabled or not)
  const endpointsByType = useMemo(() => {
    const map = {};
    channels.forEach((c) => {
      if (!map[c.type]) map[c.type] = [];
      map[c.type].push(c);
    });
    return map;
  }, [channels]);

  const grouped = useMemo(() => {
    if (!catalog) return [];
    const byCat = {};
    (catalog.events || []).forEach((e) => {
      if (!byCat[e.category]) byCat[e.category] = [];
      byCat[e.category].push(e);
    });
    return (catalog.categories || [])
      .filter((c) => byCat[c.key]?.length)
      .map((c) => ({ category: c, events: byCat[c.key] }));
  }, [catalog]);

  if (prefsLoading && !catalog) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }
  if (!catalog) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Notifications catalog isn't available right now. Try refreshing.
        </Typography>
      </Paper>
    );
  }

  const isDirty = draft && preferences
    && JSON.stringify(draft) !== JSON.stringify(preferences.events || {});

  const _eventEntry = (eventCode, defaults) => {
    if (draft && draft[eventCode]) return draft[eventCode];
    return {
      enabled:  defaults?.enabled ?? true,
      channels: defaults?.channels ?? [],
    };
  };

  const _setEventEntry = (eventCode, patch) => {
    setDraft((prev) => ({
      ...(prev || {}),
      [eventCode]: {
        ...(_eventEntry(eventCode, prev?.[eventCode])),
        ...patch,
      },
    }));
  };

  const handleEnabled = (eventCode, entry, value) => {
    _setEventEntry(eventCode, { enabled: value, channels: entry.channels });
  };

  const handleChannel = (eventCode, entry, channelKey) => {
    const current = entry.channels || [];
    const next = current.includes(channelKey)
      ? current.filter((c) => c !== channelKey)
      : [...current, channelKey];
    _setEventEntry(eventCode, { enabled: entry.enabled, channels: next });
  };

  return (
    <Stack spacing={2}>
      {/* ------------------------------------------------ header strip */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Notifications</Typography>
            <Typography variant="body2" color="text.secondary">
              Connect delivery channels, then pick which events reach you and
              where. Security and transactional emails are always sent.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              size="small" variant="outlined" disabled={!isDirty}
              onClick={() => setDraft(preferences?.events || {})}
            >
              Discard
            </Button>
            <Button
              size="small" variant="contained"
              disabled={!isDirty || prefsLoading}
              onClick={() => dispatch(updatePreferences(draft))}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ------------------------------------------ delivery channels */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
          Delivery channels
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
          In-app, email and WebSocket push are always available. Connect
          Slack, Teams, PagerDuty or a webhook to route events there too.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
          }}
        >
          {SETUP_TYPES.map((type) => {
            const eps = endpointsByType[type] || [];
            const meta = CHANNEL_META[type];
            return (
              <Paper
                key={type}
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  borderColor: eps.length
                    ? alpha(theme.palette.primary.main, 0.4)
                    : undefined,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <ChannelIcon type={type} size={22} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {meta.label}
                    </Typography>
                  </Box>
                  {eps.length > 0 && (
                    <Chip
                      size="small"
                      label={`${eps.length} connected`}
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  )}
                </Stack>
                <Typography
                  variant="caption" color="text.secondary"
                  sx={{ minHeight: 30 }}
                >
                  {meta.blurb}
                </Typography>

                <Stack spacing={0.75}>
                  {eps.map((ep) => (
                    <EndpointRow
                      key={ep.channel_id}
                      endpoint={ep}
                      test={channelTests[ep.channel_id]}
                      onToggle={(on) => dispatch(saveChannel({
                        channel_id: ep.channel_id, enabled: on,
                      }))}
                      onEdit={() => setDialog({ type, endpoint: ep })}
                      onDelete={() => dispatch(deleteChannel(ep.channel_id))}
                      onTest={() => dispatch(testChannel(ep.channel_id))}
                    />
                  ))}
                </Stack>

                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setDialog({ type, endpoint: null })}
                  sx={{ alignSelf: 'flex-start', mt: 'auto' }}
                >
                  {eps.length ? 'Add another' : 'Connect'}
                </Button>
              </Paper>
            );
          })}
        </Box>
      </Paper>

      {/* ------------------------------------------------ event matrix */}
      {grouped.map(({ category, events }) => (
        <Accordion
          key={category.key}
          defaultExpanded
          disableGutters
          variant="outlined"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {category.label}
              </Typography>
              {category.description && (
                <Typography variant="caption" color="text.secondary">
                  {category.description}
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack divider={<Divider flexItem />}>
              {events.map((event) => {
                const entry = _eventEntry(event.event_code, draft?.[event.event_code]);
                const sevColor = _toneColor(theme, SEVERITY_TONE[event.severity] || 'info');
                return (
                  <Stack
                    key={event.event_code}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ sm: 'center' }}
                    sx={{ py: 1.25 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sevColor }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {event.title}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {event.description || event.event_code}
                      </Typography>
                    </Box>

                    {/* Per-channel icon toggles */}
                    <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                      {channelDefs.map((ch) => {
                        const active = (entry.channels || []).includes(ch.key);
                        const needsSetup = ch.requires_setup
                          && !(endpointsByType[ch.key] || []).length;
                        const disabled = !entry.enabled || needsSetup;
                        const label = CHANNEL_META[ch.key]?.label || ch.label;
                        const tip = needsSetup
                          ? `Connect a ${label} endpoint above first`
                          : !entry.enabled
                            ? 'Notification is off'
                            : `${active ? 'Stop sending' : 'Send'} to ${label}`;
                        return (
                          <Tooltip key={ch.key} title={tip}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={disabled}
                                onClick={() => handleChannel(event.event_code, entry, ch.key)}
                                sx={{
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: active && !disabled
                                    ? alpha(theme.palette.primary.main, 0.5)
                                    : 'transparent',
                                  bgcolor: active && !disabled
                                    ? alpha(theme.palette.primary.main, 0.08)
                                    : 'transparent',
                                  opacity: disabled ? 0.35 : (active ? 1 : 0.45),
                                }}
                              >
                                <ChannelIcon
                                  type={ch.key}
                                  size={17}
                                  sx={{
                                    // Brand SVGs keep their colors; MUI glyphs
                                    // pick up the active tone.
                                    color: active && !disabled
                                      ? theme.palette.primary.main
                                      : theme.palette.text.secondary,
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      })}
                    </Stack>

                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!entry.enabled}
                          onChange={(e) => handleEnabled(event.event_code, entry, e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {entry.enabled ? 'On' : 'Off'}
                        </Typography>
                      }
                      sx={{ m: 0, flexShrink: 0 }}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* -------------------------------------------- endpoint dialog */}
      {dialog && (
        <EndpointDialog
          open
          type={dialog.type}
          endpoint={dialog.endpoint}
          saving={channelSaving}
          error={channelSaveError}
          onClose={() => setDialog(null)}
          onSave={(payload) => dispatch(saveChannel(payload))}
        />
      )}
    </Stack>
  );
};

export default NotificationsTab;
